/**
 * Delivery Estimation Component
 * Calculates and displays estimated delivery dates based on current time in CEST timezone
 * Handles client-side calculation to avoid Shopify caching issues
 */

class DeliveryEstimation extends HTMLElement {
  constructor() {
    super();
    this.dateElement = this.querySelector('.delivery-date-text');
    if (this.dateElement) {
      this.updateDeliveryDate();
    }
  }

  getCurrentCESTTime() {
    const now = new Date();
    const cestTimeString = now.toLocaleString('en-US', { timeZone: 'Europe/Berlin' });
    const cestTime = new Date(cestTimeString);
    const result = {
      date: cestTime,
      day: cestTime.getDay(),
      hour: cestTime.getHours(),
      timestamp: cestTime.getTime()
    };
    
    return result;
  }

  calculateDeliveryDays() {
    const { day, hour } = this.getCurrentCESTTime();
    let result;
    
    if (day === 0 || day === 6) {
      result = { startDays: 2, endDays: 3 };
      return result;
    }
    
    if (day >= 1 && day <= 3) {
      if (hour < 12) {
        result = { startDays: 1, endDays: 2 };
        return result;
      } else {
        result = { startDays: 2, endDays: 3 };
        return result;
      }
    }
    
    if (day === 4) {
      if (hour < 12) {
        result = { startDays: 1, endDays: 2 };
        return result;
      } else {
        result = { startDays: 2, endDays: 4 };
        return result;
      }
    }
    
    if (day === 5) {
      if (hour < 12) {
        result = { startDays: 1, endDays: 3 };
        return result;
      } else {
        result = { startDays: 3, endDays: 4 };
        return result;
      }
    }

    result = { startDays: 2, endDays: 3 };
    return result;
  }

  /**
   * Builds the date out of the locale files, exactly like snippets/localized-date.liquid
   * does for the server-rendered fallback. The storefront language is already resolved
   * server side by snippets/theme-js-variables.liquid, so nothing here has to guess it,
   * and merchants can translate the day names, the month names and the order of the
   * three fields in the Shopify language editor.
   *
   * strings.days is Sunday-first, to line up with Date.prototype.getDay().
   */
  formatDate(date) {
    const strings = window.Shopify?.theme?.strings?.date;
    const weekday = strings?.days?.[date.getDay()];
    const month = strings?.months?.[date.getMonth()];

    if (!weekday || !month || !strings.day_and_month) {
      return this.formatDateFallback(date);
    }

    return strings.day_and_month
      .replace('%weekday%', weekday)
      .replace('%day%', date.getDate())
      .replace('%month%', month);
  }

  /**
   * Unreachable on a normal page: both layouts that can render this block —
   * theme.liquid and theme.mobile.liquid — also render theme-js-variables.liquid,
   * which emits the date strings unconditionally. Kept as a cheap guard so a missing
   * bridge degrades to a correctly localised date instead of throwing.
   *
   * Intl takes the storefront tag as-is: de-DE, de-EU, de-AT and fr-FR all resolve,
   * so there is no locale map to keep in sync with the locale files. Only a malformed
   * tag throws, and that falls back to the default market's language.
   */
  formatDateFallback(date) {
    const options = { weekday: 'long', day: 'numeric', month: 'long' };
    const language = window.shopifyLocale || window.Shopify?.locale || document.documentElement.lang;

    try {
      return date.toLocaleDateString(language, options);
    } catch {
      return date.toLocaleDateString('de-DE', options);
    }
  }

  addDays(date, days) {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  updateDeliveryDate() {
    const { date } = this.getCurrentCESTTime();
    const { startDays, endDays } = this.calculateDeliveryDays();
    const startDate = this.addDays(date, startDays);
    const endDate = this.addDays(date, endDays);
    const formattedStartDate = this.formatDate(startDate);
    const formattedEndDate = this.formatDate(endDate);
    const template = window.Shopify?.theme?.strings?.delivery_estimation || 'Between {{ start_date }} and {{ end_date }}';
    const deliveryText = template
      .replace('{{ start_date }}', formattedStartDate)
      .replace('{{ end_date }}', formattedEndDate);
    
    this.dateElement.innerHTML = deliveryText;
    this.dataset.startDate = startDate.toISOString();
    this.dataset.endDate = endDate.toISOString();
    this.dataset.calculatedAt = new Date().toISOString();
  }
}

if (!customElements.get('delivery-estimation')) {
  customElements.define('delivery-estimation', DeliveryEstimation);
}

